import { useEffect, useState } from "react"
import { Box, Button, Card, CardContent, CircularProgress, Tab, Tabs, Typography } from "@mui/material"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import SyncIcon from "@mui/icons-material/Sync"
import { useNavigate, useParams } from "react-router-dom"
import { toast } from "react-toastify"

import { ChipStyled } from "src/shared/components"
import { usePermission } from "src/shared/hooks/usePermission"
import ROUTE_PATHS from "src/routes/route-path"
import { useIntegrationQuery, useSyncIntegrationCatalogMutation } from "src/graphql/quark/quark.type"
import { readStockBuffer } from "../../stockBuffer"
import ConfigureTab from "../../components/ConfigureTab"
import EventsTab from "../../components/EventsTab"
import LocationsTab from "../../components/LocationsTab"
import ProductsTab from "../../components/ProductsTab"
import ActivityTab from "../../components/ActivityTab"

// Carrier connectors have their own module (plan Phase 4) — cost-centre
// mapping doesn't fit the flat settings/products/stores shape of this page.
const CARRIER_CONNECTOR_CODES = new Set(["TCS", "Trax", "Leopards"])

const IntegrationDetail = () => {
  const { connectionId = "" } = useParams()
  const navigate = useNavigate()
  const { hasPermission } = usePermission()
  const canEdit = hasPermission("system:integrations:update")

  const [tab, setTab] = useState(0)

  const { data, loading } = useIntegrationQuery({
    variables: { connectionId },
    fetchPolicy: "network-only",
    skip: !connectionId
  })

  const integrationConnectorCode = data?.fusionIntegration?.connectorCode

  useEffect(() => {
    if (integrationConnectorCode && CARRIER_CONNECTOR_CODES.has(integrationConnectorCode))
      navigate(ROUTE_PATHS.FULFILLMENT_ORDERS.CARRIER_DETAIL(connectionId), {
        replace: true
      })
  }, [integrationConnectorCode, connectionId, navigate])

  const [syncCatalog, { loading: syncing }] = useSyncIntegrationCatalogMutation()

  const handleSync = async () => {
    try {
      const result = await syncCatalog({ variables: { connectionId } })
      const results = result.data?.syncFusionIntegrationCatalog ?? []

      const failed = results.filter((r) => r.error)
      if (failed.length > 0) {
        toast.error(failed[0].error ?? "Sync failed")
        return
      }

      // Only products whose stock or price changed are sent; anything over the
      // rate limit stays queued and the worker sends it shortly.
      const sent = results.reduce((n, r) => n + r.itemCount, 0)
      const queued = results.reduce((n, r) => n + r.queuedCount, 0)
      const plural = (n: number) => (n === 1 ? "" : "s")
      if (sent === 0 && queued === 0) toast.success("Everything is already up to date.")
      else if (queued === 0) toast.success(`Sent ${sent} changed product${plural(sent)}.`)
      else
        toast.info(
          `Sent ${sent} changed product${plural(sent)}; ${queued} more queued and going out over the next few minutes.`
        )
    } catch (error: any) {
      toast.error(error?.message ?? "Could not sync")
    }
  }

  if (loading)
    return (
      <Box display="flex" justifyContent="center" p={6}>
        <CircularProgress />
      </Box>
    )

  const integration = data?.fusionIntegration
  if (!integration)
    return (
      <Box p={2}>
        <Typography>Integration not found.</Typography>
      </Box>
    )

  return (
    <Box p={2}>
      <Button
        size="small"
        startIcon={<ArrowBackIcon />}
        sx={{ mb: 1 }}
        onClick={() => navigate(ROUTE_PATHS.INTEGRATIONS_HUB.BASE)}
      >
        All integrations
      </Button>

      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={2}>
        <Typography variant="h6">{integration.displayName}</Typography>
        <Button
          variant="contained"
          startIcon={<SyncIcon />}
          disabled={!canEdit || syncing}
          onClick={() => void handleSync()}
        >
          {syncing ? "Syncing…" : "Sync now"}
        </Button>
      </Box>

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent sx={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Environment
            </Typography>
            <Typography>{integration.environment}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Status
            </Typography>
            <Box>
              <ChipStyled
                size="small"
                variant="outlined"
                label={integration.status}
                color={integration.status === "ACTIVE" ? "success" : "warning"}
              />
            </Box>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Published
            </Typography>
            <Typography>
              {integration.mappedProductCount} product
              {integration.mappedProductCount === 1 ? "" : "s"} across {integration.mappedLocationCount} store
              {integration.mappedLocationCount === 1 ? "" : "s"}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      <Tabs value={tab} onChange={(_, value) => setTab(value)} sx={{ mb: 2 }}>
        <Tab label="Configure" />
        <Tab label="Stores" />
        <Tab label="Products" />
        <Tab label="Events" />
        <Tab label="Activity" />
      </Tabs>

      {tab === 0 && <ConfigureTab integration={integration} canEdit={canEdit} />}
      {tab === 1 && <LocationsTab connectionId={connectionId} canEdit={canEdit} />}
      {tab === 2 && (
        <ProductsTab
          connectionId={connectionId}
          integrationName={integration.displayName}
          defaultStockBuffer={readStockBuffer(integration.settings)}
          canEdit={canEdit}
        />
      )}
      {tab === 3 && <EventsTab connectionId={connectionId} canEdit={canEdit} />}
      {tab === 4 && <ActivityTab connectionId={connectionId} />}
    </Box>
  )
}

export default IntegrationDetail
